/**
 * Transmission — real transmission load & capacity over time.
 *  - Interconnector flow vs export/import transfer limits + utilisation
 *    (the genuine inter-regional "load and capacity" series).
 *  - Currently-active transmission line/transformer outages.
 * All real AEMO actuals from aus_warehouse (no forecast, no synthetic data).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup, Skeleton, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import { API_BASE_URL, MarketShell, useMarketData, LineChartBox } from './_shared';
import { ChartSection, StatCard, formatMW } from '../charts/shared';
import { cachedGet } from '../../utils/cachedFetch';

const tbgSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none', fontWeight: 600, fontSize: '0.74rem',
    px: 1.2, py: 0.4, borderColor: 'divider',
    '&.Mui-selected': {
      bgcolor: 'primary.main', color: '#F4F2EC',
      '&:hover': { bgcolor: 'primary.dark' },
    },
  },
};

const fmtDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};
const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
};

// Top binding network constraints involving the selected interconnector
// (from DISPATCHCONSTRAINT + GENERICCONSTRAINTRHS + GENCONDATA).
function IcConstraints({ ic, days }) {
  const [st, setSt] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    if (!ic) return;
    let alive = true;
    setSt({ loading: true, error: null, data: null });
    cachedGet(`${API_BASE_URL}/api/market/transmission_constraints`,
      { params: { ic, days } })
      .then((r) => { if (alive) setSt({ loading: false, error: null, data: r.data }); })
      .catch((e) => {
        if (alive) setSt({ loading: false, data: null,
          error: e.response?.data?.error || e.message || 'Failed to load constraints' });
      });
    return () => { alive = false; };
  }, [ic, days]);

  if (st.loading) return <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />;
  if (st.error) return <Alert severity="warning">{st.error}</Alert>;
  const rows = st.data?.constraints || [];
  if (!rows.length) {
    return (
      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
        No binding constraints involving {ic} in the last {days} days.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { fontSize: '0.78rem' } }}>
        <TableHead>
          <TableRow>
            {['Constraint', 'Description', 'Limit', 'Bind intervals',
              'Avg $/MW', 'Max $/MW', 'Last bind'].map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.constraint_id} hover>
              <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                <Tooltip title={c.reason || ''} arrow placement="right">
                  <span>{c.constraint_id}</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ color: 'text.secondary', maxWidth: 320 }}>
                {c.description || '—'}
              </TableCell>
              <TableCell>{c.limit_type || '—'}</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>{c.bind_intervals}</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>{c.avg_marginal ?? '—'}</TableCell>
              <TableCell sx={{ textAlign: 'right' }}>{c.max_marginal ?? '—'}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {c.last_bind ? new Date(c.last_bind).toLocaleString('en-AU',
                  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function MarketTransmission() {
  const [days, setDays] = useState(90);
  const { data, loading, error } = useMarketData('transmission', days);
  const [ic, setIc] = useState(null);

  const ics = data?.ics || [];
  const activeIc = ic && ics.includes(ic) ? ic : ics[0];

  const series = useMemo(() => {
    if (!data || !activeIc) return null;
    const rows = (data.flows || [])
      .filter((r) => r.ic === activeIc)
      .sort((a, b) => a.date.localeCompare(b.date));
    if (!rows.length) return null;
    const x = rows.map((r) => fmtDate(r.date));
    return {
      x,
      flow: rows.map((r) => r.avg_flow),
      exp: rows.map((r) => r.exp_lim),
      imp: rows.map((r) => r.imp_lim),
      util: rows.map((r) => r.util_pct),
      peakUtil: Math.max(...rows.map((r) => r.util_pct ?? 0)),
      avgAbs: rows.reduce((s, r) => s + (r.avg_abs_flow ?? 0), 0) / rows.length,
    };
  }, [data, activeIc]);

  let body = null;
  if (data) {
    const k = data.kpis || {};
    const outages = data.outages || [];
    body = (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <StatCard label="Active line/transformer outages"
            value={k.active_outages ?? '—'} color="#D98A2B" />
          <StatCard label="Substations affected"
            value={k.substations_affected ?? '—'} />
          <StatCard label={`Peak utilisation · ${activeIc || '—'}`}
            value={series ? `${Math.round(series.peakUtil)}%` : '—'}
            unit="window" />
          <StatCard label={`Mean |flow| · ${activeIc || '—'}`}
            value={series ? `${Math.round(series.avgAbs)} MW` : '—'} />
        </Box>

        {ics.length > 0 && (
          <ToggleButtonGroup
            size="small" exclusive value={activeIc}
            onChange={(_, v) => v && setIc(v)}
            sx={{ ...tbgSx, mb: 2, flexWrap: 'wrap' }}
          >
            {ics.map((o) => (
              <ToggleButton key={o} value={o}>{o}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <ChartSection
          title={`Flow vs transfer limit — ${activeIc || ''}`}
          subtitle="Daily mean MW. Flow sits inside the import (−) / export (+) limit envelope — real AEMO actuals">
          {series && (
            <LineChartBox
              x={series.x}
              series={[
                { data: series.exp, label: 'Export limit', color: '#94a3b8',
                  showMark: false, curve: 'linear', connectNulls: true },
                { data: series.imp, label: 'Import limit', color: '#94a3b8',
                  showMark: false, curve: 'linear', connectNulls: true },
                { data: series.flow, label: 'Actual flow', color: '#D98A2B',
                  showMark: false, curve: 'linear', connectNulls: true },
              ]}
              height={360}
              yFormat={formatMW}
            />
          )}
        </ChartSection>

        <ChartSection
          title={`Utilisation — ${activeIc || ''}`}
          subtitle="Daily mean of |flow| ÷ transfer limit in the flow direction (%)">
          {series && (
            <LineChartBox
              x={series.x}
              series={[{
                data: series.util, label: 'Utilisation %', color: '#0EA5A4',
                showMark: false, curve: 'linear', connectNulls: true,
              }]}
              height={300}
              yFormat={(v) => `${Math.round(v)}%`}
            />
          )}
        </ChartSection>

        <ChartSection
          title={`Binding network constraints involving ${activeIc || ''}`}
          subtitle="Constraints whose RHS terms reference the selected interconnector (SPD_TYPE='I'), trailing 30 days — bind count, marginal value $/MW (congestion shadow price)">
          {activeIc && <IcConstraints ic={activeIc} days={30} />}
        </ChartSection>

        <ChartSection
          title="Active transmission outages"
          subtitle="Line & transformer outages in progress now — latest submission per outage (NETWORK_OUTAGEDETAIL)">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { fontSize: '0.78rem' } }}>
              <TableHead>
                <TableRow>
                  {['Substation', 'Equip', 'Type', 'Start', 'End', 'Status', 'Reason']
                    .map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        {h}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {outages.length === 0 && (
                  <TableRow><TableCell colSpan={7}
                    sx={{ color: 'text.secondary' }}>
                    No active line/transformer outages.
                  </TableCell></TableRow>
                )}
                {outages.map((o) => (
                  <TableRow key={o.outage_id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{o.substation || '—'}</TableCell>
                    <TableCell>{o.equipment_id || '—'}</TableCell>
                    <TableCell>{o.equipment_type}</TableCell>
                    <TableCell>{fmtDT(o.start)}</TableCell>
                    <TableCell>{fmtDT(o.end)}</TableCell>
                    <TableCell>{o.status || '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 280 }}>
                      {o.reason || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </ChartSection>
      </>
    );
  }

  return (
    <MarketShell
      title="Transmission"
      subtitle="Interconnector flow vs capacity & active network outages — real AEMO actuals"
      days={days} setDays={setDays}
      loading={loading} error={error}
    >
      {body}
    </MarketShell>
  );
}
